import { useState } from 'react'
import { useAnswerIntranetIdea, useDeleteIntranetIdea, useIntranetIdeas, useSubmitIntranetIdea } from '../api/queries'
import type { IntranetIdea, IntranetIdeaStatus } from '../api/types'
import { useCreateRequest } from '../lib/createRequest'
import { formatDateTime } from '../lib/format'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

const STATUSES: { key: IntranetIdeaStatus; label: string; color: string }[] = [
  { key: 'new', label: 'Ny', color: '#64748B' },
  { key: 'considering', label: 'Undersøges', color: '#D97706' },
  { key: 'accepted', label: 'Vi gør det', color: '#2563EB' },
  { key: 'done', label: 'Gennemført', color: '#16A34A' },
  { key: 'declined', label: 'Ikke nu', color: '#9CA3AF' }
]

const colorOf = (status: IntranetIdeaStatus) => STATUSES.find((s) => s.key === status)?.color ?? '#64748B'

/**
 * Idéboksen (docs/DECISIONS.md beslutning 115): alle kan foreslå noget - også anonymt - og alle kan se idéerne og
 * ledelsens svar. Svarer gør kontoret og administratorerne.
 */
export function IdeasTab() {
  const { data, isLoading, error } = useIntranetIdeas()
  const [writing, setWriting] = useState(false)
  const [thanks, setThanks] = useState<string | null>(null)

  useCreateRequest(['ide'], () => {
    setThanks(null)
    setWriting(true)
  })

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div className="small muted">Har du en idé til at gøre noget bedre? Læg den i boksen - alle kan se svaret.</div>
        {!writing && (
          <button
            className="btn btn--primary"
            onClick={() => {
              setThanks(null)
              setWriting(true)
            }}
          >
            Ny idé
          </button>
        )}
      </div>

      {thanks && <div className="map-notice">{thanks}</div>}

      {writing && (
        <IdeaForm
          onClose={() => setWriting(false)}
          onSent={(anonymous) => {
            setWriting(false)
            setThanks(
              anonymous
                ? 'Tak for idéen! Den er lagt i boksen anonymt.'
                : 'Tak for idéen! Du får besked, når ledelsen har svaret.'
            )
          }}
        />
      )}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && data.length === 0 && <EmptyState>Boksen er tom. Vær den første - tryk "Ny idé".</EmptyState>}

      {data?.map((idea) => (
        <IdeaCard key={idea.id} idea={idea} />
      ))}
    </div>
  )
}

function IdeaForm({ onClose, onSent }: { onClose: () => void; onSent: (anonymous: boolean) => void }) {
  const submit = useSubmitIntranetIdea()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [anonymous, setAnonymous] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await submit.mutateAsync({ title: title.trim(), description: description.trim(), isAnonymous: anonymous })
      onSent(anonymous)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Idéen kunne ikke gemmes.')
    }
  }

  return (
    <Card title="Ny idé">
      <form className="stack" onSubmit={send}>
        <label className="field">
          <span className="field__label">Overskrift *</span>
          <input
            required
            maxLength={200}
            placeholder="F.eks. Fælles værktøjsvogn på lageret"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">Hvad går idéen ud på - og hvad ville den gøre bedre? *</span>
          <textarea
            required
            rows={4}
            maxLength={4000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginTop: 3 }}
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          />
          <span>
            Læg den anonymt
            <span className="tiny muted" style={{ display: 'block' }}>
              Så gemmes der intet om, hvem du er - heller ikke i loggen. Du kan stadig se idéen og svaret i boksen, men du
              får ikke besked, når der er svaret.
            </span>
          </span>
        </label>
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={submit.isPending}>
            {submit.isPending ? 'Gemmer ...' : anonymous ? 'Læg den anonymt' : 'Læg den i boksen'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

function IdeaCard({ idea }: { idea: IntranetIdea }) {
  const [answering, setAnswering] = useState(false)
  const remove = useDeleteIntranetIdea()

  async function deleteIdea() {
    if (!window.confirm(`Slet idéen "${idea.title}"?`)) return
    try {
      await remove.mutateAsync(idea.id)
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Idéen kunne ikke slettes.')
    }
  }

  return (
    <article className="card card--flat" style={{ padding: 14, borderLeft: `4px solid ${colorOf(idea.status)}` }}>
      <div className="row row--between" style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>💡 {idea.title}</div>
          <div className="tiny muted">
            {formatDateTime(idea.createdAt)} ·{' '}
            {idea.isAnonymous ? 'anonym' : (idea.createdBy?.displayName ?? 'en tidligere kollega')}
            {idea.isMine && ' (dig)'}
          </div>
        </div>
        <span className="pill" style={{ background: colorOf(idea.status), color: '#fff', flexShrink: 0 }}>
          {idea.statusName}
        </span>
      </div>

      <div className="small" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
        {idea.description}
      </div>

      {idea.answer && !answering && (
        <div className="small" style={{ marginTop: 10, padding: 10, background: '#F8FAFC', borderRadius: 8 }}>
          <strong>Svar:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{idea.answer}</span>
          {idea.answeredBy && (
            <div className="tiny muted">
              {idea.answeredBy.displayName}
              {idea.answeredAt && ` · ${formatDateTime(idea.answeredAt)}`}
            </div>
          )}
        </div>
      )}

      {answering && <AnswerForm idea={idea} onClose={() => setAnswering(false)} />}

      {(idea.canAnswer || idea.canDelete) && !answering && (
        <div className="btn-group" style={{ marginTop: 10 }}>
          {idea.canAnswer && (
            <button className="btn btn--sm btn--primary" onClick={() => setAnswering(true)}>
              {idea.answer ? 'Ret svaret' : 'Svar'}
            </button>
          )}
          {idea.canDelete && (
            <button className="btn btn--sm" disabled={remove.isPending} onClick={() => void deleteIdea()}>
              Slet
            </button>
          )}
        </div>
      )}
    </article>
  )
}

function AnswerForm({ idea, onClose }: { idea: IntranetIdea; onClose: () => void }) {
  const answer = useAnswerIntranetIdea()
  const [status, setStatus] = useState<IntranetIdeaStatus>(idea.status === 'new' ? 'considering' : idea.status)
  const [text, setText] = useState(idea.answer ?? '')
  const [error, setError] = useState<string | null>(null)

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await answer.mutateAsync({ id: idea.id, status, answer: text })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Svaret kunne ikke gemmes.')
    }
  }

  return (
    <form className="stack" style={{ marginTop: 10 }} onSubmit={save}>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {STATUSES.filter((s) => s.key !== 'new').map((s) => (
          <button
            key={s.key}
            type="button"
            className={`btn btn--sm ${status === s.key ? 'btn--primary' : ''}`}
            onClick={() => setStatus(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <label className="field">
        <span className="field__label">Svar{status === 'declined' ? ' *' : ''}</span>
        <textarea
          rows={3}
          maxLength={4000}
          required={status === 'declined'}
          placeholder={status === 'declined' ? 'Hvorfor ikke? F.eks. Ikke i år - vi tager den op i foråret' : 'F.eks. God idé - vi køber en vogn i oktober'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {error && <div className="error-box">{error}</div>}
      <div className="btn-group">
        <button className="btn btn--sm btn--primary" type="submit" disabled={answer.isPending}>
          {answer.isPending ? 'Gemmer ...' : 'Gem svaret'}
        </button>
        <button className="btn btn--sm" type="button" onClick={onClose}>
          Fortryd
        </button>
      </div>
    </form>
  )
}
